/**
 * Utility functions for transforming data between API and frontend formats
 */

/**
 * Convert snake_case to camelCase
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

/**
 * Convert camelCase to snake_case
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * Transform object keys from snake_case to camelCase
 */
export const transformSnakeToCamel = <T = any>(obj: any): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformSnakeToCamel(item)) as any;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {};
    
    Object.keys(obj).forEach(key => {
      const camelKey = snakeToCamel(key);
      transformed[camelKey] = transformSnakeToCamel(obj[key]);
    });
    
    return transformed;
  }

  return obj;
};

/**
 * Transform object keys from camelCase to snake_case
 */
export const transformCamelToSnake = <T = any>(obj: any): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformCamelToSnake(item)) as any;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {};
    
    Object.keys(obj).forEach(key => {
      const snakeKey = camelToSnake(key);
      transformed[snakeKey] = transformCamelToSnake(obj[key]);
    });
    
    return transformed;
  }

  return obj;
};

/**
 * Transform framework data from API format to frontend format
 */
export const transformFramework = (framework: any) => {
  return {
    ...framework,
    createdAt: framework.created_at || framework.createdAt,
    updatedAt: framework.updated_at || framework.updatedAt,
    createdBy: framework.created_by || framework.createdBy,
    updatedBy: framework.updated_by || framework.updatedBy,
  };
};

/**
 * Transform multiple frameworks
 */
export const transformFrameworks = (frameworks: any[]) => {
  return frameworks.map(transformFramework);
};

/**
 * Transform obligation data from API format to frontend format
 */
export const transformObligation = (obligation: any) => {
  return {
    ...obligation,
    // Map title to name for frontend compatibility
    name: obligation.title || obligation.name || '',
    title: obligation.title || obligation.name || '',
    createdAt: obligation.created_at || obligation.createdAt,
    updatedAt: obligation.updated_at || obligation.updatedAt,
    createdBy: obligation.created_by || obligation.createdBy,
    updatedBy: obligation.updated_by || obligation.updatedBy,
    frameworkId: obligation.framework_id || obligation.frameworkId,
  };
};

/**
 * Transform multiple obligations
 */
export const transformObligations = (obligations: any[]) => {
  return obligations.map(transformObligation);
};

/**
 * Transform control data from API format to frontend format
 */
export const transformControl = (control: any) => {
  return {
    ...control,
    createdAt: control.created_at || control.createdAt,
    updatedAt: control.updated_at || control.updatedAt,
    createdBy: control.created_by || control.createdBy,
    updatedBy: control.updated_by || control.updatedBy,
    obligationId: control.obligation_id || control.obligationId,
  };
};

/**
 * Transform multiple controls
 */
export const transformControls = (controls: any[]) => {
  return controls.map(transformControl);
};

/**
 * Transform requirement data from API format to frontend format
 */
export const transformRequirement = (requirement: any) => {
  return {
    ...requirement,
    // Map title to name for frontend compatibility
    name: requirement.title || requirement.name || '',
    title: requirement.title || requirement.name || '',
    createdAt: requirement.created_at || requirement.createdAt,
    updatedAt: requirement.updated_at || requirement.updatedAt,
    createdBy: requirement.created_by || requirement.createdBy,
    updatedBy: requirement.updated_by || requirement.updatedBy,
    controlId: requirement.control_id || requirement.controlId,
    dueDate: requirement.due_date || requirement.dueDate,
  };
};

/**
 * Transform multiple requirements
 */
export const transformRequirements = (requirements: any[]) => {
  return requirements.map(transformRequirement);
};

/**
 * Transform task data from API format to frontend format
 */
export const transformTask = (task: any) => {
  return {
    ...task,
    createdAt: task.created_at || task.createdAt,
    updatedAt: task.updated_at || task.updatedAt,
    createdBy: task.created_by || task.createdBy,
    updatedBy: task.updated_by || task.updatedBy,
    requirementId: task.requirement_id || task.requirementId,
    dueDate: task.due_date || task.dueDate,
  };
};

/**
 * Transform multiple tasks
 */
export const transformTasks = (tasks: any[]) => {
  return tasks.map(transformTask);
};

/**
 * Transform document data from API format to frontend format
 */
export const transformDocument = (document: any) => {
  return {
    ...document,
    createdAt: document.created_at || document.createdAt,
    updatedAt: document.updated_at || document.updatedAt,
    createdBy: document.created_by || document.createdBy,
    updatedBy: document.updated_by || document.updatedBy,
    requirementId: document.requirement_id || document.requirementId,
    fileName: document.file_name || document.fileName,
    fileUrl: document.file_url || document.fileUrl,
  };
};

/**
 * Transform multiple documents
 */
export const transformDocuments = (documents: any[]) => {
  return documents.map(transformDocument);
};
